import { locate } from "@dhx/ts-common/html";
import { isEqual } from "./helpers/other";
import { stateFromDom } from "./helpers/selection";
import { Editor } from "./models/Editor";
import { IRichTextEventSystem, ISelectionPosition, ISelectionState, RichTextEvents } from "./types";

type CompactRange = [number, number, number];

function getPositionFromRange(range): ISelectionPosition {
	const pos: ISelectionPosition = {};
	let rect = range.getBoundingClientRect();
	if (rect.height === 0) {
		rect = range.getClientRects()[0];
	}
	pos.xStart = rect.left + window.pageXOffset;
	pos.yStart = rect.top + window.pageYOffset;
	range.collapse(false);
	rect = range.getClientRects()[0];
	pos.xEnd = rect.left + window.pageXOffset;
	pos.yEnd = rect.top + window.pageYOffset;
	return pos;
}
export class SelectionHelper {
	private _uid: string;

	private _events: IRichTextEventSystem;
	private _state: ISelectionState;

	private _waitUpdate: boolean;

	private _editor: Editor;
	private _ignoreSelection: boolean;
	private _ignoreSelectionTimeout: any;

	private _selectedText: string;

	constructor(editor, uid, events) {
		this._events = events;
		this._uid = uid;
		this._editor = editor;
		this.clear();

		this._initEvents();
	}
	clear() {
		this._state = {
			range: false,
			left: {
				blockIndex: 0,
				textIndex: 0,
				offset: 0,
			},
		};
		this._waitUpdate = true;
	}
	set(state: ISelectionState) {
		this._state = state;
		this._waitUpdate = true;
	}
	get() {
		return this._state;
	}
	getCompact(): CompactRange[] {
		const state = this._state;
		const compact = [[state.left.blockIndex, state.left.textIndex, state.left.offset] as CompactRange];
		if (this._state.range) {
			compact.push([state.right.blockIndex, state.right.textIndex, state.right.offset] as CompactRange);
		}
		return compact;
	}
	setCompact(compact: CompactRange[]) {
		const state: ISelectionState = {
			left: {
				blockIndex: compact[0][0],
				textIndex: compact[0][1],
				offset: compact[0][2],
			},
			range: compact.length === 2,
		};
		if (state.range) {
			state.right = {
				blockIndex: compact[1][0],
				textIndex: compact[1][1],
				offset: compact[1][2],
			};
		}
		this.set(state);
	}
	getPosition(selection: ISelectionState): Promise<any> {
		return new Promise(res => {
			if (!this._waitUpdate) {
				const range = this._createRange(selection);
				res(getPositionFromRange(range));
			}
			const ctx = new Date();
			this._events.on(
				RichTextEvents.selectionRefresh,
				() => {
					this._events.detach(RichTextEvents.selectionRefresh, ctx);
					const range = this._createRange(selection);
					res(getPositionFromRange(range));
				},
				ctx
			);
		});
	}
	destructor() {
		document.removeEventListener("selectionchange", this._selectionChange);
	}
	isActive() {
		const { anchorNode } = window.getSelection();
		return locate(anchorNode as HTMLElement, "dhx_widget_id") === this._uid;
	}
	isFull(): boolean {
		const { left, right, range } = this._state;
		if (!range) {
			return false;
		}
		const last = this._editor.blocks.length - 1;
		const end = this._editor.blocks[last].getBlockEnd();
		return (
			left.blockIndex === 0 &&
			left.textIndex === 0 &&
			left.offset === 0 &&
			right.blockIndex === last &&
			right.textIndex === end.textIndex &&
			right.offset === end.offset
		);
	}
	update() {
		if (!this._waitUpdate) {
			return true;
		}

		return this.refresh();
	}
	getSelectedText() {
		return this._selectedText;
	}
	selectAll() {
		// silent select
		const blocks = this._editor.blocks;
		this._state = {
			range: true,
			left: {
				blockIndex: 0,
				textIndex: 0,
				offset: 0,
			},
			right: {
				blockIndex: blocks.length - 1,
				...blocks[blocks.length - 1].getBlockEnd(),
			},
		};
	}
	refresh(): boolean {
		const { left, right, range } = this._state;

		const selection = window.getSelection();
		if (!selection) {
			return false;
		}
		if (this._ignoreSelectionTimeout) {
			clearTimeout(this._ignoreSelectionTimeout);
		}

		this._ignoreSelection = true;
		this._ignoreSelectionTimeout = setTimeout(() => {
			this._ignoreSelection = false;
			this._ignoreSelectionTimeout = null;
		}, 5);

		let selectionRange: Range;
		if (selection.rangeCount > 0) {
			selectionRange = selection.getRangeAt(0);
			selection.removeAllRanges();
		} else {
			selectionRange = document.createRange();
		}

		const startNode = this._editor.getTextElement(left.blockIndex, left.textIndex);
		if (!startNode) {
			return false;
		}
		let endNode;

		selectionRange.setStart(startNode, Math.min(left.offset, startNode.length));
		if (range) {
			endNode = this._editor.getTextElement(right.blockIndex, right.textIndex);
			if (!endNode) {
				return false;
			}
			selectionRange.setEnd(endNode, Math.min(right.offset, endNode.length));
		} else {
			selectionRange.setEnd(startNode, Math.min(left.offset, startNode.length));
		}

		selection.addRange(selectionRange);

		this._waitUpdate = false;
		this._events.fire(RichTextEvents.selectionRefresh, []);
		return true;
	}
	private _initEvents() {
		document.addEventListener("selectionchange", this._selectionChange);
	}
	private _createRange(selection: ISelectionState): Range {
		const { left, right } = selection;
		const range = document.createRange();
		const startNode = this._editor.getTextElement(left.blockIndex, left.textIndex);
		range.setStart(startNode, left.offset);
		if (selection.range) {
			const endNode = this._editor.getTextElement(right.blockIndex, right.textIndex);
			range.setEnd(endNode, right.offset);
		} else {
			range.setEnd(startNode, left.offset);
		}
		return range;
	}
	private _selectionChange = () => {
		const isActive = this.isActive();
		if (isActive) {
			this._selectedText = window.getSelection().toString();
			if (this._ignoreSelection) {
				return;
			}
			const state = stateFromDom(this._editor);
			if (isEqual(state, this._state, true)) {
				return;
			}
			this._state = state;
			this._events.fire(RichTextEvents.selectionChange, [state]);
		}
	};
}
